/**
 * Customer portal API client — deliberately separate from lib/api.js.
 *
 * The portal is a distinct auth surface from staff login (see backend
 * routers/customer_portal.py's require_portal_user vs core.require_user):
 * a portal JWT can never resolve on a staff endpoint and vice versa. Using
 * a SEPARATE localStorage key and axios instance means a staff member and
 * a customer can be logged in in the same browser without either session
 * clobbering the other's token, and a portal-session 401 redirects to
 * /portal/login instead of staff's /login.
 */
import axios from 'axios';
import { BACKEND_URL } from './api';

const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'portal_access_token';

export const portalApi = axios.create({ baseURL: API, withCredentials: false });

portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

portalApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && !(err.config?.url || '').includes('/portal/login')) {
      localStorage.removeItem(TOKEN_KEY);
      if (!window.location.pathname.startsWith('/portal/login')) {
        window.location.href = '/portal/login';
      }
    }
    return Promise.reject(err);
  }
);

export const setPortalToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearPortalToken = () => localStorage.removeItem(TOKEN_KEY);
export const getPortalToken = () => localStorage.getItem(TOKEN_KEY);

export const portalGet = (p, params) => portalApi.get(p, { params }).then((r) => r.data);
export const portalPost = (p, body) => portalApi.post(p, body).then((r) => r.data);
export const portalPatch = (p, body) => portalApi.patch(p, body).then((r) => r.data);
